import { getIn, useFormikContext } from 'formik'
import React, { useEffect, useMemo } from 'react'
import {
  getSectionStatus,
  StatusIcon,
} from '../../../FlyOutCloudSettings/StepStatusIcon'
import { getClientSecretExpirationStatus } from '../../../ClientSecretExpirationBanner/getClientSecretExpirationStatus'

export const MicrosoftStepStatusIcon: React.FC<{ sectionName: string }> = ({
  sectionName,
}) => {
  const formik = useFormikContext<Record<string, Record<string, unknown>>>()

  const sectionErrors = formik ? getIn(formik.errors, sectionName) : {}
  const sectionTouched = formik ? getIn(formik.touched, sectionName) : {}
  const sectionCompleted = getIn(formik.values.steps, `${sectionName}`)
  const doesPartnerTypeHaveValue =
    getIn(formik.values.mpcAccountDetails, 'isISV') ||
    getIn(formik.values.mpcAccountDetails, 'isConsultingPartner')

  useEffect(() => {
    if (sectionName === 'microsoftProductDetails') {
      const shouldComplete = !!doesPartnerTypeHaveValue && !sectionErrors
      if (
        getIn(formik.values.steps, 'microsoftProductDetails') !== shouldComplete
      ) {
        formik.setFieldValue(`steps.microsoftProductDetails`, shouldComplete)
      }
      return
    }
    const currentCompleted = !!getIn(formik.values.steps, `${sectionName}`)
    if (
      !sectionErrors &&
      !currentCompleted &&
      sectionName !== 'linkMpcAccount'
    ) {
      formik.setFieldValue(`steps.${sectionName}`, true)
    } else if (sectionErrors && currentCompleted) {
      formik.setFieldValue(`steps.${sectionName}`, false)
    }
  }, [sectionName, sectionErrors, doesPartnerTypeHaveValue])
  // Helper: Checks if a touched field has a corresponding error (supports nested objects/arrays)
  const hasVisibleErrors = (errors: any, touched: any): boolean => {
    if (typeof touched === 'boolean') {
      // Base case: A field is touched and has an error string
      return touched && typeof errors === 'string'
    }

    if (Array.isArray(touched)) {
      // Handle arrays (e.g., productDetails[])
      return touched.some((_, i) => hasVisibleErrors(errors?.[i], touched[i]))
    }

    if (typeof touched === 'object' && touched !== null) {
      // Handle nested objects (e.g., microsoftProductDetails.productDetails)
      return Object.keys(touched).some(key =>
        hasVisibleErrors(errors?.[key], touched[key])
      )
    }

    return false
  }

  const doesSectionHaveErrors = hasVisibleErrors(sectionErrors, sectionTouched)

  const isClientSecretExpired = useMemo(() => {
    if (sectionName !== 'cloudCredentials') {
      return false
    }

    const clientSecretExpirationDate = getIn(
      formik.values,
      'cloudCredentials.clientSecretExpirationDate'
    ) as string | undefined

    return (
      getClientSecretExpirationStatus(clientSecretExpirationDate, false)
        ?.type === 'expired'
    )
  }, [formik.values, sectionName])

  const status = getSectionStatus(
    sectionCompleted,
    doesSectionHaveErrors || isClientSecretExpired
  )

  return <StatusIcon status={status} />
}
